import { useEffect, useState, useRef } from 'react';
import Loading from '../common/Loading';
import styles from '../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../stores/useProductSelectionStore';
import useMultiOpeningMatStore from '../../stores/useMultiOpeningMatStore';
import PhotoNavigation from './PhotoNavigation';
import StatusBar from './StatusBar';
import PhotoPagination from './PhotoPagination';
import PhotoThumbnails from './PhotoThumbnails';
import PhotoCropper from './PhotoCropper';
import { getEmptyStateMessage } from '../../lib/utils';
import MultiOpeningMatVisualization from './MultiOpeningMatVisualization';

const PhotoDisplay = ({ lineItemId, isLoading }) => {

  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const cropperRef = useRef(null);
  const { addToCart, photos, selectedPhoto, selectAllPhotos, photosLoadingMessage, form, setForm, productType } = useProductSelectionStore();
  const { selectedProduct: multiOpeningSelectedProduct } = useMultiOpeningMatStore();

  // Calculate aspect ratio from x/y values
  useEffect(() => {
    if (form.meta.yAspectRatio === undefined) return

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        aspectRatio: prevState.meta.xAspectRatio / prevState.meta.yAspectRatio,
      },
    }))
  }, [form?.meta?.xAspectRatio, form?.meta?.yAspectRatio])

  if (addToCart) {
    return <Loading message={lineItemId ? "Updating Order" : "Adding to cart..."} />;
  }

  const handleCropBoxRotate = () => {
    const cropper = cropperRef.current?.cropper
    if (!cropper) return

    const hasAspectRatio = form?.meta?.aspectRatio !== undefined

    if (hasAspectRatio) {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          xAspectRatio: prevState.meta.yAspectRatio,
          yAspectRatio: prevState.meta.xAspectRatio,
        },
      }))
    } else {
      cropper.rotate(90)
      requestAnimationFrame(() => {
        const containerData = cropper.getContainerData()
        const canvasData = cropper.getCanvasData()
        
        if (!containerData || !canvasData) return
        
        // Center the canvas horizontally and position at top
        const left = (containerData.width - canvasData.width) / 2
        const top = 45
        
        cropper.setCanvasData({
          left: left,
          top: top,
        })
      })
    }
  }

  const onCrop = (event) => {
    const cropper = cropperRef.current?.cropper
    const cropData = cropper?.getData()
    const imageData = cropper?.getImageData()
    const originalWidth = imageData?.naturalWidth
    const originalHeight = imageData?.naturalHeight
    const cropXPercent = ((cropData.x < 0 ? 0 : cropData.x) / originalWidth) * 100
    const cropYPercent = ((cropData.y < 0 ? 0 : cropData.y) / originalHeight) * 100
    const cropWPercent = (cropData.width / originalWidth) * 100
    const cropHPercent = (cropData.height / originalHeight) * 100

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        cropPositionX: cropXPercent,
        cropPositionY: cropYPercent,
        cropWidth: cropWPercent,
        cropHeight: cropHPercent,
      },
    }))
  }

  return (
    <div
      className={`product-selection-picture-container ${lineItemId ? styles.editModeContainer : ''}`}
      onMouseEnter={() => setIsHoveringImage(true)}
      onMouseLeave={() => setIsHoveringImage(false)}
    >
      {!multiOpeningSelectedProduct && !isLoading && <PhotoNavigation isHoveringImage={isHoveringImage} />}

      {/* <StatusBar onSaveChanges={onSaveChanges} /> */}
      {(photosLoadingMessage || isLoading) && (
          <Loading message={photosLoadingMessage || 'Loading...'} />
        )}

      <div className={`product-selection-main-picture-container ${styles.mainPictureContainerScrollable}`}>
      {photosLoadingMessage && (
          <Loading message={photosLoadingMessage} />  
        )}
        {(productType === 'albums' || photos.length === 0) && !multiOpeningSelectedProduct && !photosLoadingMessage && !isLoading && (
          <div className={styles.emptyPhotosState}>
            <p>No photos yet.</p>
            <p>{getEmptyStateMessage(productType)}</p>
          </div>
        )}

        {productType === 'multi-opening' && multiOpeningSelectedProduct && !photosLoadingMessage && !isLoading && (
          <MultiOpeningMatVisualization />
        )}
       
        {(productType !== 'multi-opening' || !multiOpeningSelectedProduct) && productType !== 'albums' && photos.length > 0 && !photosLoadingMessage && !isLoading && selectedPhoto?.attributes && (
          <div className="main-picture-grid">
            <div
              className={`slider-mask w-slider-mask ${styles.sliderMaskContainer}`}
              id="w-slider-mask-0"
            >
              {photos.map((photo, index) => {
                const currentIndex = photos.findIndex(p => p.id === selectedPhoto?.id);
                const isActive = selectedPhoto?.id === photo.id;

                return (
                  <div
                    key={photo.id}
                    className={`slide-2 slide-one w-slide ${styles.slideItem} ${index === 0 ? styles.slideItemFirst : styles.slideItemAbsolute}`}
                    aria-label={`${index + 1} of ${photos.length}`}
                    role="group"
                    style={{
                      transform: `translateX(${(index - currentIndex) * 100}%)`,
                    }}
                    aria-hidden={!isActive}
                  >
                    <div className="div-block-345" aria-hidden={!isActive}>
                      {isActive && !selectAllPhotos ? (
                        <PhotoCropper
                          photo={photo}
                          cropperRef={cropperRef}
                          onCrop={onCrop}
                        />
                      ) : (
                        <img
                          src={photo?.attributes?.presignedPhotoUrl}
                          loading="lazy"
                          alt={photo?.attributes?.photoFileName}
                          className={`image-91 ${styles.placeholderImage}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              <div
                aria-live="off"
                aria-atomic="true"
                className={`w-slider-aria-label ${styles.srOnly}`}
                data-wf-ignore=""
              >
                Slide {photos.findIndex(p => p.id === selectedPhoto?.id) + 1} of {photos.length}.
              </div>
            </div>
          </div>
        )}
      </div>

      {productType !== 'albums' && !multiOpeningSelectedProduct && photos.length > 0 && !isLoading && (
        <PhotoPagination onRotate={handleCropBoxRotate} />
      )}

      {productType !== 'albums' && photos.length > 0 && !lineItemId && !isLoading && (
        <PhotoThumbnails />
      )}
    </div>
  );
};

export default PhotoDisplay;
